import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import {
  Building2,
  Check,
  ExternalLink,
  Globe2,
  Mail,
  MapPin,
  Phone,
  Plus,
  Send,
  UserRound,
  X,
  type LucideIcon,
} from 'lucide-react'
import type { Lead } from '../../types/crm'
import { leadKeyFor } from '../../data/unifiedLeads'
import { compactUrl, firstUrl, formatCurrency, formatDate } from '../../utils/format'
import { getLeadDisplayName } from './leadDisplay'
import {
  LeadCrmMeta,
  LeadInteractions,
  LeadSocialProfiles,
  LogInteractionModal,
  messageOf,
  useLeadCrm,
  type SheetSocialSave,
  type SourceSocialLink,
} from './LeadCrm'
import { saveLeadCrmProfile, type LeadInteraction } from './leadCrmClient'
import { LeadOrganizationEdit } from './LeadOrganizationEdit'
import { applyLeadSourceValues, editableSourceFields, saveLeadSource } from './leadSourceClient'
import { LeadWebsiteLogo } from './LeadWebsiteLogo'
import { SendEmailModal } from './SendEmailModal'
import './SendEmailModal.css'
import {
  getLeadEmail,
  getLeadNeeds,
  getLeadPhone,
  getLeadProfile,
  getLeadWebsite,
  isProspectOnly,
} from './unifiedLeadFields'

const UNAVAILABLE_VALUES = new Set([
  '',
  '-',
  ' - ',
  'â€“',
  'n/a',
  'na',
  'none',
  'not available',
  'does not have a website',
  'no website',
])

export function hasMeaningfulValue(value: unknown): boolean {
  if (value === null || value === undefined) return false
  if (typeof value === 'number') return Number.isFinite(value)
  const normalized = String(value).trim().toLowerCase().replace(/[.!]+$/, '')
  return !UNAVAILABLE_VALUES.has(normalized)
}

function hasPositiveNumber(value: number | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}

function InformationItem({
  Icon,
  label,
  children,
}: {
  Icon: LucideIcon
  label: string
  children: ReactNode
}) {
  return (
    <div className="lead-modal-info-item" title={label}>
      <Icon aria-label={label} role="img" size={18} />
      <div>{children}</div>
    </div>
  )
}

function ExternalInformationItem({ Icon, label, value }: { Icon: LucideIcon; label: string; value: string }) {
  const url = firstUrl(value)
  if (!url) return null

  return (
    <InformationItem Icon={Icon} label={label}>
      <a aria-label={`Open ${label}`} href={url} target="_blank" rel="noreferrer">
        <span>{compactUrl(value)}</span>
        <ExternalLink aria-hidden="true" size={13} />
      </a>
    </InformationItem>
  )
}

function OpportunityFact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="lead-modal-fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  )
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function LeadDetailModal({
  lead: loadedLead,
  edited,
  onClose,
  onLeadUpdated,
}: {
  lead: Lead
  /** The lead as last saved to its sheet in this session, if it was edited. */
  edited?: Lead
  onClose: () => void
  onLeadUpdated?: (lead: Lead) => void
}) {
  const [lead, setLead] = useState(edited ?? loadedLead)
  const [editingSource, setEditingSource] = useState(false)
  const organization = lead.organization
  const opportunity = lead.opportunity
  const displayName = getLeadDisplayName(lead)
  const [isClosing, setIsClosing] = useState(false)
  const [composeOpen, setComposeOpen] = useState(false)
  const [logInteractionOpen, setLogInteractionOpen] = useState(false)
  const [toast, setToast] = useState('')
  const toastTimerRef = useRef<number | null>(null)
  const [noteDraft, setNoteDraft] = useState<string | null>(null)
  const [noteSaving, setNoteSaving] = useState(false)
  const [noteError, setNoteError] = useState('')
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const closeTimerRef = useRef<number | null>(null)
  const closingRef = useRef(false)

  // The CRM overlay is keyed by the same reconciled identity the rest of the
  // app uses to recognise a prospect, so nothing is stored against a row number.
  // The key stays the loaded lead's even after an Organization edit, so the
  // overlay does not detach mid-session when an identity field changes.
  const leadKey = useMemo(() => leadKeyFor(loadedLead), [loadedLead])
  const crm = useLeadCrm(leadKey)
  const crmProfile = crm.state.status === 'ready' ? crm.state.data.profile : null

  const beginClose = useCallback(() => {
    if (closingRef.current) return
    closingRef.current = true
    setIsClosing(true)
    closeTimerRef.current = window.setTimeout(onClose, prefersReducedMotion() ? 0 : 180)
  }, [onClose])

  useEffect(() => {
    const appShell = document.querySelector<HTMLElement>('.app-shell')
    const previousOverflow = document.body.style.overflow
    appShell?.classList.add('is-lead-modal-open')
    document.body.style.overflow = 'hidden'
    closeButtonRef.current?.focus()

    return () => {
      appShell?.classList.remove('is-lead-modal-open')
      document.body.style.overflow = previousOverflow
      if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current)
      if (toastTimerRef.current !== null) window.clearTimeout(toastTimerRef.current)
    }
  }, [])

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      // A dialog above this modal closes itself first.
      if (event.key === 'Escape' && !composeOpen && !logInteractionOpen) beginClose()
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [beginClose, composeOpen, logInteractionOpen])

  // A prospecting-only lead has no CRM organization, so its own contact details
  // are used. Nothing is invented when a field is simply absent.
  const prospectOnly = isProspectOnly(lead)
  const website = getLeadWebsite(lead)
  const email = getLeadEmail(lead)
  const phone = getLeadPhone(lead)
  const profile = getLeadProfile(lead)
  const needs = getLeadNeeds(lead)
  const showProfile = Boolean(profile)
    && !hasMeaningfulValue(organization?.facebook)
    && !hasMeaningfulValue(organization?.linkedin)
  const location = [organization?.city, organization?.state]
    .filter(hasMeaningfulValue)
    .join(', ')
  const prospectOwner =
    hasMeaningfulValue(opportunity?.name) &&
    !/^not identified$/i.test(opportunity?.name.trim() ?? '')
      ? opportunity?.name.trim() ?? ''
      : hasMeaningfulValue(lead.owner) &&
          !/^not identified$/i.test(lead.owner.trim())
        ? lead.owner.trim()
        : ''
  const hasOpportunityInformation = Boolean(opportunity && (
    hasMeaningfulValue(opportunity.name)
    || hasMeaningfulValue(lead.priority)
    || hasMeaningfulValue(opportunity.source)
    || hasPositiveNumber(opportunity.value)
    || hasMeaningfulValue(opportunity.lossReason)
    || hasMeaningfulValue(opportunity.dateCollected)
  ))
  const sheetNote = hasMeaningfulValue(opportunity?.notes)
    ? opportunity?.notes ?? ''
    : hasMeaningfulValue(organization?.notes) ? organization?.notes ?? '' : ''

  // A saved CRM note becomes the canonical note, including an empty one: a note
  // the user deliberately cleared stays cleared instead of falling back to the
  // Sheet note. Only an absent row or a null crm_note falls back, and the Sheet
  // is never written to from here.
  const hasCrmNote = crmProfile !== null && crmProfile.crmNote !== null
  const displayedNote = hasCrmNote ? crmProfile.crmNote ?? '' : sheetNote

  // Sheet social links as the sheets provide them; placeholders such as NA
  // are no profile. Facebook/Instagram/LinkedIn are editable Sheet columns.
  const sourceSocials = useMemo<SourceSocialLink[]>(
    () =>
      [
        { label: 'Facebook', value: organization?.facebook ?? '', sheetField: 'facebook' as const },
        { label: 'Instagram', value: organization?.instagram ?? '', sheetField: 'instagram' as const },
        { label: 'LinkedIn', value: organization?.linkedin ?? '', sheetField: 'linkedin' as const },
        ...(showProfile ? [{ label: 'Profile', value: profile, sheetField: undefined }] : []),
      ]
        .filter((candidate) => hasMeaningfulValue(candidate.value))
        .map((candidate) => ({ label: candidate.label, url: firstUrl(candidate.value) ?? '', sheetField: candidate.sheetField }))
        .filter((candidate) => candidate.url),
    [organization?.facebook, organization?.instagram, organization?.linkedin, profile, showProfile],
  )

  // Only a lead with an Organizations row has Sheet social columns to write.
  const saveSheetSocial: SheetSocialSave | undefined = organization
    ? async (field, url) => {
        const next = applyLeadSourceValues(lead, await saveLeadSource(lead, { [field]: url }))
        setLead(next)
        onLeadUpdated?.(next)
      }
    : undefined

  async function saveNote() {
    setNoteError('')
    setNoteSaving(true)

    try {
      const saved = await saveLeadCrmProfile(leadKey, { crmNote: noteDraft ?? '' })
      crm.update((current) => ({ ...current, profile: saved }))
      setNoteDraft(null)
    } catch (cause) {
      setNoteError(messageOf(cause, 'The note could not be saved.'))
    } finally {
      setNoteSaving(false)
    }
  }

  function handleInteractionSaved(interaction: LeadInteraction) {
    crm.update((current) => ({ ...current, interactions: [interaction, ...current.interactions] }))
    // A short-lived confirmation instead of a notice that stays in the section.
    setToast('Interaction logged')
    if (toastTimerRef.current !== null) window.clearTimeout(toastTimerRef.current)
    toastTimerRef.current = window.setTimeout(() => {
      toastTimerRef.current = null
      setToast('')
    }, 3200)
    // The dialog closes itself after its exit animation.
  }

  return createPortal(
    <>
    <div className={`lead-modal-layer${isClosing ? ' is-closing' : ''}`} data-testid="lead-modal-layer">
      <button
        aria-label="Close lead details"
        className="lead-modal-backdrop"
        onClick={beginClose}
        type="button"
      />
      <section className="lead-modal lead-detail-modal" role="dialog" aria-modal="true" aria-labelledby="lead-detail-title">
        <header className="lead-modal-header">
          <LeadWebsiteLogo website={website} />
          <div className="lead-modal-heading">
            <h2 id="lead-detail-title">{displayName}</h2>
            <p>Available information about this prospect</p>
          </div>
          <button
            className="icon-button lead-modal-close"
            onClick={beginClose}
            ref={closeButtonRef}
            title="Close"
            type="button"
            aria-label="Close lead details"
          >
            <X size={21} />
          </button>
        </header>

        <LeadCrmMeta crm={crm}>
          {/* Sending is available only for a lead that has an email. */}
          {hasMeaningfulValue(email) ? (
            <button className="button button-secondary send-email-trigger" onClick={() => setComposeOpen(true)} type="button">
              <Send aria-hidden="true" size={14} />
              Send Email
            </button>
          ) : null}
          <button
            className="button button-primary"
            disabled={crm.state.status !== 'ready'}
            onClick={() => {
              setLogInteractionOpen(true)
            }}
            type="button"
          >
            <Plus aria-hidden="true" size={14} />
            Log Interaction
          </button>
        </LeadCrmMeta>

        <div className="lead-modal-content">
          <div className="lead-modal-columns">
            <div className="lead-modal-column">
              <section className="lead-modal-section" aria-labelledby="lead-organization-title">
                <div className="lead-crm-heading">
                  <h3 id="lead-organization-title">{prospectOnly ? 'Prospect' : 'Organization'}</h3>
                  {!editingSource && editableSourceFields(lead).length ? (
                    <button aria-label={`Edit ${prospectOnly ? 'prospect' : 'organization'} details`} className="lead-crm-action" onClick={() => setEditingSource(true)} type="button">
                      Edit
                    </button>
                  ) : null}
                </div>
                {editingSource ? (
                  <LeadOrganizationEdit
                    lead={lead}
                    onCancel={() => setEditingSource(false)}
                    onSaved={(values) => {
                      const next = applyLeadSourceValues(lead, values)
                      setLead(next)
                      setEditingSource(false)
                      onLeadUpdated?.(next)
                    }}
                  />
                ) : (
                <div className="lead-modal-information-grid">
                  <InformationItem
                    Icon={prospectOnly ? UserRound : Building2}
                    label={prospectOnly ? 'Prospect' : 'Organization'}
                  >
                    {displayName}
                  </InformationItem>
                  {prospectOwner && prospectOwner !== displayName ? (
                    <InformationItem Icon={UserRound} label="Owner">{prospectOwner}</InformationItem>
                  ) : null}
                  <ExternalInformationItem Icon={Globe2} label="Website" value={website} />
                  {hasMeaningfulValue(email) ? (
                    <InformationItem Icon={Mail} label="Email">
                      <span>{email.trim()}</span>
                    </InformationItem>
                  ) : null}
                  {hasMeaningfulValue(phone) ? (
                    <InformationItem Icon={Phone} label="Phone">
                      <a href={`tel:${phone.replace(/[^+\d]/g, '')}`}>{phone}</a>
                    </InformationItem>
                  ) : null}
                  {location ? <InformationItem Icon={MapPin} label="Location">{location}</InformationItem> : null}
                </div>
                )}
              </section>

              <LeadSocialProfiles crm={crm} saveSheetSocial={saveSheetSocial} sourceLinks={sourceSocials} />

              {hasOpportunityInformation && opportunity ? (
                <section className="lead-modal-section" aria-labelledby="lead-opportunity-title">
                  <h3 id="lead-opportunity-title">Opportunity</h3>
                  <dl className="lead-modal-facts">
                    {hasMeaningfulValue(opportunity.name) ? <OpportunityFact label="Opportunity Name">{opportunity.name}</OpportunityFact> : null}
                    {hasMeaningfulValue(lead.priority) ? <OpportunityFact label="Priority">{lead.priority}</OpportunityFact> : null}
                    {hasMeaningfulValue(opportunity.source) ? <OpportunityFact label="Source">{opportunity.source}</OpportunityFact> : null}
                    {hasPositiveNumber(opportunity.value) ? <OpportunityFact label="Value">{formatCurrency(opportunity.value)}</OpportunityFact> : null}
                    {hasMeaningfulValue(opportunity.lossReason) ? <OpportunityFact label="Loss Reason">{opportunity.lossReason}</OpportunityFact> : null}
                    {hasMeaningfulValue(opportunity.dateCollected) ? <OpportunityFact label="Date Collected">{formatDate(opportunity.dateCollected ?? '')}</OpportunityFact> : null}
                  </dl>
                </section>
              ) : null}

              {needs ? (
                <section className="lead-modal-section lead-modal-notes" aria-labelledby="lead-needs-title">
                  <h3 id="lead-needs-title">Needs</h3>
                  <p>{needs}</p>
                </section>
              ) : null}
            </div>

            <div className="lead-modal-column">

              <section className="lead-modal-section lead-modal-notes" aria-labelledby="lead-notes-title">
                <div className="lead-crm-heading">
                  <h3 id="lead-notes-title">Notes</h3>
                  {noteDraft === null ? (
                    <button
                      className="lead-crm-action"
                      disabled={crm.state.status !== 'ready'}
                      onClick={() => {
                        setNoteError('')
                        setNoteDraft(displayedNote)
                      }}
                      type="button"
                    >
                      Edit
                    </button>
                  ) : null}
                </div>

                {noteDraft === null ? (
                  // A cleared CRM note renders as the empty note it is; the Sheet
                  // note only appears when there is no CRM note at all.
                  hasCrmNote || displayedNote ? (
                    <p>{displayedNote}</p>
                  ) : (
                    <p className="lead-crm-state">
                      {crm.state.status === 'loading' ? 'Loading CRM details...' : 'No notes yet.'}
                    </p>
                  )
                ) : (
                  <form
                    className="lead-crm-form"
                    onSubmit={(event) => {
                      event.preventDefault()
                      saveNote()
                    }}
                  >
                    <label className="lead-crm-field" htmlFor="lead-crm-note">
                      <span className="sr-only">CRM note</span>
                      <textarea
                        autoFocus
                        id="lead-crm-note"
                        maxLength={10000}
                        onChange={(event) => setNoteDraft(event.target.value)}
                        rows={5}
                        value={noteDraft}
                      />
                    </label>

                    {noteError ? <p className="lead-crm-error" role="alert">{noteError}</p> : null}

                    <div className="lead-crm-form-actions">
                      <button className="button button-secondary lead-crm-small" disabled={noteSaving} type="submit">
                        {noteSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className="lead-crm-action"
                        disabled={noteSaving}
                        onClick={() => {
                          setNoteError('')
                          setNoteDraft(null)
                        }}
                        type="button"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </section>

              <LeadInteractions crm={crm} />
            </div>
          </div>
        </div>
      </section>
    </div>
    {composeOpen ? <SendEmailModal lead={lead} onClose={() => setComposeOpen(false)} /> : null}
    {logInteractionOpen ? (
      <LogInteractionModal
        leadKey={leadKey}
        staff={crm.state.status === 'ready' ? crm.state.data.staff : []}
        onClose={() => setLogInteractionOpen(false)}
        onSaved={handleInteractionSaved}
      />
    ) : null}
    {toast ? (
      <div className="lead-toast" role="status">
        <Check aria-hidden="true" size={15} strokeWidth={2.4} />
        {toast}
      </div>
    ) : null}
    </>,
    document.body,
  )
}
